/**
 * ListsOverview — shows all user lists with create/edit/delete.
 * Shared across all tracker apps.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserView from '../hooks/useUserView.jsx';
import useCurrentUser from '../hooks/useCurrentUser.jsx';
import { EmojiDropdown } from '@code-burrow/design-system';

export default function ListsOverview({ backendService }) {
    const [lists, setLists] = useState([]);
    const [itemCounts, setItemCounts] = useState({});
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmoji, setNewEmoji] = useState('');
    const [createError, setCreateError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editEmoji, setEditEmoji] = useState('');
    const navigate = useNavigate();
    const { activeView } = useUserView();
    const currentUser = useCurrentUser();
    // Lists are personal — use the authenticated user's name, not the active pod/combined view
    const listUserName = currentUser?.name || activeView;

    const refresh = useCallback(async () => {
        const result = await backendService.getAllLists(listUserName);
        setLists(result.lists || []);
        // Fetch item counts
        const counts = {};
        for (const list of result.lists || []) {
            const itemsResult = await backendService.getListItems(list.id);
            counts[list.id] = itemsResult.items?.length || 0;
        }
        setItemCounts(counts);
    }, [backendService, listUserName]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreateError(null);
        try {
            await backendService.createList(listUserName, { name: newName.trim(), emoji: newEmoji.trim() });
            setNewName('');
            setNewEmoji('');
            setCreating(false);
            refresh();
        } catch (err) {
            setCreateError(err.message || 'Failed to create list');
        }
    };

    const handleDelete = async (e, listId) => {
        e.stopPropagation();
        await backendService.deleteList(listId);
        refresh();
    };

    const startEditing = (e, list) => {
        e.stopPropagation();
        setEditingId(list.id);
        setEditName(list.name);
        setEditEmoji(list.emoji || '');
    };

    const handleEdit = async () => {
        if (!editName.trim()) return;
        await backendService.updateList(editingId, { name: editName.trim(), emoji: editEmoji.trim() });
        setEditingId(null);
        refresh();
    };

    return (
        <div className="space-y-6">
            {/* List cards */}
            <div className="grid gap-3">
                {lists.map((list) => (
                    <div key={list.id}>
                        {editingId === list.id ? (
                            <div className="flex items-center gap-2 p-4 rounded-lg bg-bg-secondary border border-border-subtle">
                                <EmojiDropdown value={editEmoji} onChange={setEditEmoji} placeholder="🎯" />
                                <input
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
                                    className="flex-1 bg-bg-secondary text-text-primary rounded-md px-3 py-1.5 text-sm border border-border placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                    autoFocus
                                />
                                <button onClick={handleEdit} className="px-3 py-1.5 text-xs rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover">Save</button>
                                <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs rounded-md text-text-secondary hover:text-text-primary">Cancel</button>
                            </div>
                        ) : (
                            <div
                                onClick={() => navigate(`/lists/${list.id}`)}
                                className="flex items-center justify-between p-4 rounded-lg bg-bg-secondary border border-border-subtle hover:border-border cursor-pointer transition-colors duration-150"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-xl">{list.emoji || '📋'}</span>
                                    <div>
                                        <h3 className="text-sm font-medium text-text-primary">{list.name}</h3>
                                        <span className="text-xs text-text-tertiary">{itemCounts[list.id] || 0} items</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={(e) => startEditing(e, list)}
                                        className="p-1.5 rounded text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors"
                                        title="Edit list"
                                    >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => handleDelete(e, list.id)}
                                        className="p-1.5 rounded text-text-tertiary hover:text-red-400 hover:bg-surface-hover transition-colors"
                                        title="Delete list"
                                    >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                    <svg className="h-4 w-4 text-text-tertiary ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Create form */}
            {creating ? (
                <>
                    <div className="flex items-center gap-2 p-4 rounded-lg bg-bg-secondary border border-border-subtle">
                        <EmojiDropdown value={newEmoji} onChange={setNewEmoji} placeholder="🎯" />
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            placeholder="List name..."
                            className="flex-1 bg-bg-secondary text-text-primary rounded-md px-3 py-1.5 text-sm border border-border placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                            autoFocus
                        />
                        <button onClick={handleCreate} className="px-3 py-1.5 text-xs rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover">Create</button>
                        <button onClick={() => { setCreating(false); setNewName(''); setNewEmoji(''); setCreateError(null); }} className="px-3 py-1.5 text-xs rounded-md text-text-secondary hover:text-text-primary">Cancel</button>
                    </div>
                    {createError && <p className="mt-2 text-xs text-red-400">{createError}</p>}
                </>
            ) : (
                <button
                    onClick={() => setCreating(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-border-subtle text-sm text-text-tertiary hover:text-text-secondary hover:border-border transition-colors duration-150"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New List
                </button>
            )}
        </div>
    );
}
